import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { filter, take } from 'rxjs';
import { ModalCheckinComponent, ModalCheckinData, ModalCheckinResult } from './modal-checkin.component';
import { UserService } from 'projects/lib-shared-core/src/lib/features/user/services/user.service';
import { SvcToastService } from '@solvace/component/svc-toast';
import { TranslocoService } from '@ngneat/transloco';
import { CheckinFilterService } from 'projects/lib-shared-core/src/lib/services/checkin-filter/checkin-filter.service';
import { ChecklistAnalyticsService } from 'projects/lib-shared-core/src/lib/services/analytics/checklist/checklist-analytics.service';
import { TasksCategory } from 'projects/lib-shared-core/src/lib/features/main-tasks/models/tasks-category';
import { SvcFeatureToggleService } from 'projects/lib-shared-core/src/lib/services/svc-feature-toggle/svc-feature-toggle.service';

@Injectable({ providedIn: 'root' })
export class ModalCheckinService {
  private _dialogRef?: MatDialogRef<ModalCheckinComponent, ModalCheckinResult>;

  constructor(
    private readonly _dialog: MatDialog,
    private readonly _userService: UserService,
    private readonly _toastService: SvcToastService,
    private readonly _transloco: TranslocoService,
    private readonly _checkinFilterService: CheckinFilterService,
    private readonly _checklistAnalyticsService: ChecklistAnalyticsService,
    private readonly _featureToggleService: SvcFeatureToggleService
  ) {
    this._userService.user$.subscribe((user) => {
      if (!user) {
        this.close();
      }
    });
  }

  public open(
    data?: ModalCheckinData,
    config?: MatDialogConfig
  ): MatDialogRef<ModalCheckinComponent, ModalCheckinResult> | undefined {
    if (this._dialogRef) return this._dialogRef;

    this._checkinFilterService
      .refreshFromCachedEndpoint()
      .pipe(take(1))
      .subscribe(() => this._openDialog(data, config));

    return this._dialogRef;
  }

  private _openDialog(
    data?: ModalCheckinData,
    config?: MatDialogConfig
  ): MatDialogRef<ModalCheckinComponent, ModalCheckinResult> | undefined {
    if (this._dialogRef) return this._dialogRef;

    const dialogData = this._buildInitialData(data);
    const dialogConfig: MatDialogConfig<ModalCheckinData> = {
      width: '400px',
      autoFocus: false,
      data: dialogData,
      ...config,
    };

    this._dialogRef = this._dialog.open(ModalCheckinComponent, dialogConfig);

    this._dialogRef
      .afterClosed()
      .pipe(filter((r) => !!r))
      .subscribe((result) => this._updateCheckinFilter(result));

    this._dialogRef.afterClosed().subscribe(() => (this._dialogRef = undefined));
    return this._dialogRef;
  }

  public openIfNeeded(data?: ModalCheckinData, config?: MatDialogConfig): void {
    if (this.shouldSkip()) return;
    this.open(data, config);
  }

  public close(): void {
    this._dialogRef?.close();
    this._dialogRef = undefined;
  }

  public shouldSkip(): boolean {
    const filterData = this._userService.user?.checkInFilter;

    if (!this._featureToggleService.checkinIsEnabled) return true;
    if (filterData?.dontAskAgain) return true;

    return false;
  }

  private _buildInitialData(data?: ModalCheckinData): ModalCheckinData {
    const user = this._userService.user;
    const filterData = user?.checkInFilter;
    const selection = this._checkinFilterService.getSelectionSnapshot();
    const active = filterData?.active;
    const defaultFilter = filterData?.default;

    const activePlace = active ? (active.udaId ?? undefined) : undefined;
    const activeEquipment = active ? (active.equipmentId ?? undefined) : undefined;
    const defaultPlace = defaultFilter ? (defaultFilter.udaId ?? undefined) : undefined;
    const defaultEquipment = defaultFilter ? (defaultFilter.equipmentId ?? undefined) : undefined;
    const selectionUdaId = selection ? (selection.udaId ?? undefined) : undefined;
    const selectionEquipmentId = selection ? (selection.equipmentId ?? undefined) : undefined;

    const selectedPlaceId: string | number | undefined =
      data?.selectedPlaceId ?? selectionUdaId ?? activePlace ?? defaultPlace;
    const selectedEquipmentId: string | number | undefined =
      data?.selectedEquipmentId ?? selectionEquipmentId ?? activeEquipment ?? defaultEquipment;

    const normalizedPlaceId = this._toNumber(selectedPlaceId);
    const normalizedEquipmentId = this._toNumber(selectedEquipmentId);

    this._checkinFilterService.updateSelection(normalizedPlaceId, normalizedEquipmentId);

    return {
      ...data,
      selectedPlaceId,
      selectedEquipmentId,
      dontAskAgain: data?.dontAskAgain ?? filterData?.dontAskAgain ?? false,
    };
  }

  private _updateCheckinFilter(result: ModalCheckinResult | undefined): void {
    if (!result) return;
    const payload = {
      setAsDefault: result?.dontAskAgain ?? false,
      dontAskAgain: result?.dontAskAgain ?? false,
      udaId: this._toNumber(result?.placeId),
      equipmentId: this._toNumber(result?.equipmentId),
    };
    this._userService
      .updateCheckinFilter(payload)
      .pipe(take(1))
      .subscribe({
        next: () => {
          const currentUser = this._userService.user;
          if (!currentUser) return;
          this._checkinFilterService.updateSelection(payload.udaId, payload.equipmentId, {
            udaName: result?.placeName ?? null,
            equipmentName: result?.equipmentName ?? null,
          });

          this._userService.user = {
            ...currentUser,
            checkIn: {
              setAsDefault: payload.dontAskAgain,
              udaId: payload.udaId ?? undefined,
              equipmentId: payload.equipmentId ?? undefined,
            },
            checkInFilter: {
              ...(currentUser.checkInFilter),
              dontAskAgain: payload.dontAskAgain,
              active: payload.udaId || payload.equipmentId ? { udaId: payload.udaId ?? undefined, equipmentId: payload.equipmentId ?? undefined } : null,
              default: payload.udaId || payload.equipmentId ? currentUser.checkInFilter?.default ?? null : null,
              recent: payload.udaId || payload.equipmentId ? currentUser.checkInFilter?.recent ?? [] : [],
            },
            checkinPlaceId: payload.udaId ?? undefined,
            checkinEquipmentId: payload.equipmentId ?? undefined,
            checkinDefault: false,
          };
          this._refreshChecklists();
        },
        error: () => {
          this._toastService.error(this._transloco.translate('Ocorreu um erro, tente novamente em outro momento'));
        },
      });
  }

  private _toNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private _refreshChecklists(): void {
    this._checklistAnalyticsService.getInspectionIndicators().subscribe();
    this._checklistAnalyticsService.getInspectionApprovalsIndicator().subscribe();
    this._checklistAnalyticsService.getInspectionCategorizedDrillDown(TasksCategory.TODAY).subscribe();
  }
}
